import { IconButton, Tooltip, Typography } from "@mui/material"
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CustomerAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import { useLocation, useNavigate } from "react-router-dom";
import { Document, DocumentAdd, DocumentEdit } from "../../../../models/document";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";
import SettingsIcon from '@mui/icons-material/Settings';
import { DocumentStructure } from "../../../../models/document-structure";
import MuiInput from "@shared/ui/mui-input/MuiInput";
import { DocumentSettings } from "../../../../models/document-settings";

// theme
import { useTheme } from "@emotion/react";
import { ItemsFormType, PaymentsFormType, WorksFormType } from "../../pages/add-document/AddDocument.types";
import { DocumentAccountAdd } from "../../../../models/document-account";
import { Account } from "../../../../models/account";

import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { FieldsRow } from '@shared/ui/layout/FieldsRow';
import { getValueByPath } from '@shared/lib/utils/object';

//state properties:
interface LocationState {

    reference?: string | null;
    customer?: DocumentAccountAdd | null;
    document?: Document | null;
}


interface DocumentCustomerProps {

    method: "add" | "edit";

    documentForm: UseFormReturn<DocumentAdd> | UseFormReturn<DocumentEdit>;
    worksForm: UseFormReturn<WorksFormType>
    itemsForm: UseFormReturn<ItemsFormType>
    paymentsForm: UseFormReturn<PaymentsFormType>

    settings: DocumentSettings;
    structure: DocumentStructure[];

    isDocumentClosed?: boolean;
}




function DocumentCustomer({ method, documentForm, worksForm, itemsForm, paymentsForm, settings, structure, isDocumentClosed }: DocumentCustomerProps) {


    const navigate = useNavigate();

    const { state } = useLocation() as { state: LocationState; }; // state data from another page
    const theme = useTheme();

    function renderAccountField<T extends FieldValues>(field: DocumentStructure, documentForm: UseFormReturn<T>) {

        const isAccountNumberField = field.field_name === "document_account.account_number";
        const shouldDisableCustomerAccountField = state?.reference || (!settings?.display_items && !settings?.display_works && settings?.display_payments && documentForm.getValues("document_account.account_number" as Path<T>));
        const isCustomerField = field.type === "חשבון" && field.show_form;

        
        // if (isCustomerField && isAccountNumberField && shouldDisableCustomerAccountField) {
        //     return <MuiInput
        //         key={field.field_name}
        //         field={field}
        //         variant='outlined'
        //         useForm={documentForm}
        //         showRequired
        //         disabled
        //     />;
        // }


        if (isCustomerField) {

            // return <AccountSearch 
            //             key={field.field_name}
            //             accountHebType={"לקוח-ספק"}
            //             fieldStructure={field}
            //             useForm={documentForm}
            //             onSelect={onCustomerSelect}
            //             prefixPath={"document_account"}
            //             disabled={isDocumentClosed}
            //         />
            return <MuiInput
                key={field.field_name}
                field={field}
                variant='outlined'
                useForm={documentForm}
                prefix="document_account"
                showRequired
                disabled={isDocumentClosed}
                searchType="לקוח-ספק"
                onSelect={onCustomerSelect}
            />;
        }

        // Return null if none of the conditions are met
        return null;
    }

    
    function createState(){
        return {
            ...state,
            document_settings_id: settings?.id,

            document_account: documentForm.getValues().document_account,
            car: documentForm.getValues().car,

            works: worksForm.getValues().works,
            items: itemsForm.getValues().items,
            payments: paymentsForm.getValues().payments,
            reference: documentForm.getValues().reference,
        }
    }

    
    function createBreadcrumbData(state: any){

        const thisPageLink = 'id' in documentForm.getValues() ?
            `/documents/${(documentForm as  UseFormReturn<DocumentEdit>).getValues("id")}` :
            `/documents/add?documentType=${settings?.id}`;
            

        return [
            {
                name: "מסמכים",
                link: "/documents",
            },
            {
                name: "הוספת מסמך",
            },
            {
                name: `${settings?.name}`,
                link: thisPageLink,
                state: state
            },
        ];
    }


    function browseCustomer() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate(`/customers`, {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "בחירת לקוח/ספק" }]
            }
        });
    }


    function editCustomer() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate(`/customers/${documentForm.getValues().document_account?.account_id || documentForm.getValues().document_account?.id}`, {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "עריכת כרטיס לקוח/ספק" }]
            }
        });
    }


    function addCustomer() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate(`/customers/add`, {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הוספת כרטיס לקוח/ספק" }]
            }
        });
    }


    function editAccountStructure() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate(`/documents/structure/account?documentType=${settings.id}`, {
            replace: true,
            state: {
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה" }, { name: "לקוח/ספק" }]
            }
        });
    }


    function onCustomerSelect(customer: Account){

        const documentCustomer: DocumentAccountAdd = {...customer, id: undefined, account_id: customer.id};
        const form = documentForm as UseFormReturn<DocumentAdd>;

        structure
            ?.filter(field => field.type === "חשבון" && field.show_form)
            .forEach(field => {
                const value = getValueByPath<any>(documentCustomer, field.field_name) ?? null;
                form.setValue(`document_account.${field.field_name}` as Path<DocumentAdd>, value, { shouldDirty: true });
            });

        form.setValue("document_account", documentCustomer, { shouldDirty: true });
    }


    /**
     * clean the account fields
     */
    function cleanFields(){
        const form = documentForm as UseFormReturn<DocumentAdd>;

        structure
            ?.filter(field => field.type === "חשבון" && field.show_form)
            .forEach(field => {
                form.setValue(`document_account.${field.field_name}` as Path<DocumentAdd>, null, { shouldDirty: true });
            });

        form.setValue("document_account", null);
    }
    


    if (method === "add") {
        return (
            <>
                <div className="title">
                    <div>
                        <Typography variant="body1" component="span">
                            פרטי לקוח:
                        </Typography>

                        <Tooltip title="בחירת כרטיס לקוח-ספק מתוך דפדוף" placement="top">
                            <IconButton onClick={browseCustomer} sx={{ color: theme.colors.main_color }}>
                                <AutoStoriesIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="הוספת כרטיס לקוח-ספק" placement="top">
                            <span>
                                <IconButton onClick={addCustomer} disabled={!!documentForm.getValues()?.document_account?.account_id} sx={{ color: theme.colors.main_color }}>

                                    <CustomerAddIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="עריכת כרטיס לקוח-ספק" placement="top">
                            <span>
                                <IconButton onClick={editCustomer} disabled={!documentForm.getValues()?.document_account?.account_id} sx={{ color: theme.colors.main_color }}>
                                    <EditIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="איפוס שדות" placement="top">
                            <span>
                                <IconButton onClick={cleanFields} sx={{ color: theme.colors.main_color }}>
                                    <DeleteSweepIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                    <div>
                        <Tooltip title="הגדרת שדות לקוח-ספק" placement="top">
                            <IconButton onClick={editAccountStructure} sx={{ color: theme.colors.main_color }}>
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                <FieldsRow>
                    {structure?.sort((a, b) => (a.location || 0) - (b.location || 0)).map(field => renderAccountField(field, documentForm as UseFormReturn<DocumentAdd>))}
                </FieldsRow>
            </>
        )
    }


    else if (method === "edit") {
        return (
            <>
                <div className="title">
                    <div>
                        <Typography variant="body1" component="span" sx={{
                            color: !isDocumentClosed ? "text.primary" : "action.disabled",
                        }}>
                            פרטי לקוח:
                        </Typography>

                        <Tooltip title="בחירת כרטיס לקוח-ספק מתוך דפדוף" placement="top">
                            <span>
                                <IconButton onClick={browseCustomer} sx={{ color: theme.colors.main_color }} disabled={isDocumentClosed}>
                                    <AutoStoriesIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="הוספת כרטיס לקוח-ספק" placement="top">
                            <span>
                                <IconButton onClick={addCustomer} disabled={!!state?.customer?.id || !!state?.document?.document_account?.account_id || isDocumentClosed} sx={{ color: theme.colors.main_color }}>
                                    <CustomerAddIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="עריכת כרטיס לקוח-ספק" placement="top">
                            <span>
                                <IconButton onClick={editCustomer} disabled={!state?.customer?.id && !state?.document?.document_account?.account_id || isDocumentClosed} sx={{ color: theme.colors.main_color }}>
                                    <EditIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="איפוס שדות" placement="top">
                            <span>
                                <IconButton onClick={cleanFields} disabled={isDocumentClosed} sx={{ color: theme.colors.main_color }}>
                                    <DeleteSweepIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                    <div>
                        <Tooltip title="הגדרת שדות לקוח-ספק" placement="top">
                            <IconButton onClick={editAccountStructure} sx={{ color: isDocumentClosed? "rgba(0,0,0,0.3)" : theme.colors.main_color }}>
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                <FieldsRow>
                    {structure?.sort((a, b) => (a.location || 0) - (b.location || 0)).map(field => renderAccountField(field, documentForm as UseFormReturn<DocumentEdit>))}
                </FieldsRow>
            </>
        )
    }

}

export default DocumentCustomer